import { __decorate } from "tslib";
// Core
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
// Extends
// Children
import BaseInnerLoader from '@/components/v2/BaseInnerLoader';
// Models / Helpers
import { EventBus, EventBusTriggers } from '@/helpers/EventBus';
import { AgentType } from '@/helpers/entities/enums/v3/Agent';
// Queries / Mutations
let AgentBreakdown = class AgentBreakdown extends Vue {
    constructor() {
        super(...arguments);
        this.isLoading = true;
        this.isLoadingMore = false;
        this.isFirstRender = true;
        this.responses = [];
        this.elementHeight = 0;
        this.scrollTop = 0;
    }
    onSelectedRecipientChanged() {
        this.responses = [];
        this.isLoading = true;
    }
    onResponsesChanged(val, oldVal) {
        setTimeout(() => {
            const container = this.$refs.agentBreakdown;
            // If the new value is an empty array, the component will be "rendered" for the first time
            if (val.length === 0) {
                this.isFirstRender = true;
            }
            if (!container) {
                return;
            }
            this.$nextTick(() => {
                container.scrollTop = this.scrollTop + container.scrollHeight - this.elementHeight;
                this.elementHeight = container.scrollHeight;
                if (this.isFirstRender && val.length !== 0) {
                    this.isFirstRender = false;
                    container.scrollTop = container.scrollHeight;
                }
            });
        }, 50);
    }
    handleScroll(event) {
        const { scrollTop } = this.$refs.agentBreakdown;
        if (scrollTop <= 0 && event.wheelDeltaY > 0) {
            this.isLoadingMore = true;
            EventBus.$emit(EventBusTriggers.loadMessengerMessages);
        }
    }
    handleTouchScroll() {
        const { scrollTop } = this.$refs.agentBreakdown;
        if (scrollTop <= 0) {
            this.isLoadingMore = true;
            EventBus.$emit(EventBusTriggers.loadMessengerMessages);
        }
    }
    setMessages(payload) {
        this.scrollTop = this.$refs.agentBreakdown?.scrollTop;
        this.responses = this.transformData(payload);
        this.isLoading = false;
        this.isLoadingMore = false;
    }
    transformData(payload) {
        return payload
            .filter((message) => message.agentBreakdown !== null)
            .map((message) => {
            return {
                ...message,
                agentBreakdown: this.buildAgentBreakdown(message),
            };
        });
    }
    buildAgentBreakdown(message) {
        const breakdown = [];
        // Filter out product_inquiry, product, orchestrator, conversation, and catalog agents.
        // This to prevent duplicate entries in the breakdown
        // because agents with reflection handle in the reflectionAgents array.
        breakdown.push(...message.agentBreakdown
            .filter((agent) => agent.type !== AgentType.product &&
            agent.type !== AgentType.product_inquiry &&
            agent.type !== AgentType.orchestrator &&
            agent.type !== AgentType.conversation &&
            agent.type !== AgentType.catalog)
            .map((agent) => {
            return {
                name: this.agentName(agent),
                class: this.agentClass(agent),
                response: this.agentResponse(agent),
            };
        }));
        // Add agents here in reverse order of execution so splicing is easier
        // Otherwise agentIndex will need to base on `breakdown` array
        const reflectionAgents = [
            AgentType.order,
            AgentType.catalog,
            AgentType.conversation,
            AgentType.sentiment,
            AgentType.product_inquiry,
            AgentType.orchestrator,
        ];
        reflectionAgents.forEach((agentName) => {
            const agentIndex = message.agentBreakdown.findIndex((agent) => {
                return agent.type === agentName;
            });
            const agent = message.agentBreakdown[agentIndex];
            // Skip reflection processing for sentiment agents
            if (agent?.reflection) {
                breakdown.splice(agentIndex, 1, ...this.agentReflection(agent));
            }
        });
        return breakdown;
    }
    agentName(agent) {
        return agent.type.replace('Prompts::', '').replace('Agent', ' Agent');
    }
    agentClass(agent) {
        switch (agent.type) {
            case AgentType.conversation:
                return 'response';
            default:
                return 'agent';
        }
    }
    agentResponse(agent) {
        if (agent.jsonCompletion !== null) {
            if (agent.type === AgentType.sentiment) {
                return (agent?.jsonCompletion?.analysis?.explanation ||
                    agent?.jsonCompletion?.response?.analysis?.explanation ||
                    agent?.completion);
            }
            return agent?.jsonCompletion?.answer;
        }
        if (this.isProductPayload(agent)) {
            return `Found the following products: ${agent.addedProducts.join(', ')}`;
        }
        if (agent.type === AgentType.order && agent.toolCalls !== null) {
            return this.parseOrderAgentTools(agent);
        }
        if (agent.completion === null) {
            return 'N/A';
        }
        if (agent.type === AgentType.guard && agent.completion === 'PASS') {
            return 'This is a valid inquiry.';
        }
        if (agent.type === AgentType.supervisor && agent.completion === 'PASS') {
            return 'This response is satisfactory.';
        }
        return agent.completion;
    }
    agentReflection(agent) {
        return agent.reflection.map((generation) => {
            let output = generation.output.join('\n');
            if (output === 'PASS') {
                output = 'This response is satisfactory.';
            }
            try {
                if (agent.type === AgentType.sentiment) {
                    const jsonOutput = JSON.parse(output);
                    output = jsonOutput?.analysis?.explanation || jsonOutput?.response?.analysis?.explanation;
                }
                else {
                    const jsonOutput = JSON.parse(output);
                    output = jsonOutput?.answer || output;
                }
            }
            catch {
                // Invalid JSON, use original output
            }
            return {
                name: generation.role === 'reflection' ? 'Supervisor Agent' : this.agentName(agent),
                class: 'agent',
                response: output,
            };
        });
    }
    isProductPayload(agent) {
        // [<number>, <number>, <number>]
        const regex = /\[\d+(, \d+)+\]/;
        return (agent.type === AgentType.product && (agent.toolCalls !== null || regex.test(agent.completion)));
    }
    parseOrderAgentTools(agent) {
        const products = agent.toolCalls.flatMap((tools) => {
            return tools.arguments.products;
        });
        return `Searching for products: ${products.join(', ')}`;
    }
    addMessages(payload) {
        this.responses += this.transformData(payload);
        this.isLoading = false;
    }
    mounted() {
        EventBus.$on(EventBusTriggers.setAgentBreakdown, this.setMessages);
        EventBus.$on(EventBusTriggers.addAgentBreakdown, this.addMessages);
    }
    // Just to be completely sure we don't get memory leaks
    beforeDestroy() {
        EventBus.$off(EventBusTriggers.setAgentBreakdown, this.setMessages);
        EventBus.$off(EventBusTriggers.addAgentBreakdown, this.addMessages);
    }
};
__decorate([
    Getter('selectedRecipient')
], AgentBreakdown.prototype, "selectedRecipient", void 0);
__decorate([
    Watch('selectedRecipient')
], AgentBreakdown.prototype, "onSelectedRecipientChanged", null);
__decorate([
    Watch('responses')
], AgentBreakdown.prototype, "onResponsesChanged", null);
AgentBreakdown = __decorate([
    Component({
        components: {
            BaseInnerLoader,
        },
    })
], AgentBreakdown);
export default AgentBreakdown;
